import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  // History entries for the drawer own their scroll position. Normal page
  // entries retain the browser's existing restoration mode.
  #opening = false;
  #trackedOpen = false;
  #historyClose = false;
  #backPending = false;
  #historyChanged = false;
  #pageRestoration = 'auto';
  /** @type {Promise<void> | null} */
  #closing = null;
  #nativeCloseDialog = this.closeDialog;

  // Keep Horizon's close implementation, but share an in-flight close so
  // a history event and an X/Escape click cannot close the same dialog twice.
  closeDialog = () => {
    if (this.#closing) return this.#closing;
    this.#closing = this.#nativeCloseDialog().finally(() => {
      this.#closing = null;
      this.#historyClose = false;
      if (this.#historyChanged) this.#syncHistory();
    });
    return this.#closing;
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('popstate', this.#handlePopState);
    window.addEventListener('pageshow', this.#handlePageShow);

    // Old theme entries have no stored position and cannot be replayed.
    // New entries retain their state across reload / full-page navigation;
    // pageshow restores them after the document has loaded.
    if (history.state?.cartDrawerOpen && !this.#historyEntry) {
      const state = { ...history.state };
      delete state.cartDrawerOpen;
      delete state.harvenCartDrawer;
      history.replaceState(state, '');
    }
    if (document.readyState === 'complete') this.#syncHistory();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  get #historyEntry() {
    const entry = history.state?.harvenCartDrawer;
    return history.state?.cartDrawerOpen && entry?.version === 1 &&
      Number.isFinite(entry.scrollY) && entry.scrollY >= 0 &&
      ['auto', 'manual'].includes(entry.pageRestoration)
      ? entry : null;
  }

  showDialog() {
    if (this.refs.dialog.open || this.#opening || this.#closing || this.#backPending) return;

    let entry = this.#historyEntry;
    if (!entry && isMobileBreakpoint()) {
      entry = {
        version: 1,
        scrollY: window.scrollY,
        pageRestoration: history.scrollRestoration,
      };
      // Push BEFORE Horizon fixes the body and the document reports scrollY=0.
      history.pushState({ ...history.state, cartDrawerOpen: true, harvenCartDrawer: entry }, '');
    }
    this.#trackedOpen = Boolean(entry);
    if (entry) {
      this.#pageRestoration = entry.pageRestoration;
      // This changes only the drawer entry, not the preceding page entry.
      // Back restores the normal page; Forward is handled by #syncHistory.
      history.scrollRestoration = 'manual';
    }
    this.#opening = true;
    super.showDialog();
  }

  #handleHistoryOpen = () => {
    this.#opening = false;
    if (this.#historyChanged) this.#syncHistory();
  };

  #handleHistoryClose = () => {
    const tracked = this.#trackedOpen;
    this.#trackedOpen = false;
    if (this.#historyClose) return;
    if (tracked && this.#historyEntry) {
      this.#backPending = true;
      history.back();
    } else if (tracked) {
      // A different theme feature may have replaced this entry's state.
      history.scrollRestoration = this.#pageRestoration;
    }
  };

  #handlePageShow = () => {
    this.#syncHistory();
  };

  #handlePopState = () => {
    this.#backPending = false;
    this.#syncHistory();
  };

  #syncHistory() {
    if (this.#opening || this.#closing || this.#backPending) {
      this.#historyChanged = true;
      return;
    }
    this.#historyChanged = false;
    const entry = this.#historyEntry;
    if (entry) {
      if (!this.refs.dialog.open) {
        // One navigation restoration before locking, never a post-close retry.
        window.scrollTo({ top: entry.scrollY, behavior: 'instant' });
        this.showDialog();
      }
    } else if (this.refs.dialog.open && this.#trackedOpen) {
      this.#historyClose = true;
      const { dialog } = this.refs;
      dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      this.closeDialog().finally(() => {
        dialog.style.removeProperty('--dialog-drawer-closing-animation');
      });
    }
  }

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
